<template>
  <div class="pdf-preview-container">
    <!-- 加载状态 -->
    <div v-if="isLoading" class="loading-mask">
      <div class="loading-spinner"></div>
      <p class="loading-text">报告加载中...</p>
    </div>

    <div class="pdf-content" :style="{ transform: `scale(${scale})`, transformOrigin: 'top center' }">
      <canvas v-for="pageNum in totalPages" :key="pageNum" :ref="el => el && canvasRefs.set(pageNum, el)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, defineProps, onUnmounted, onMounted } from 'vue'
import { message } from 'ant-design-vue'

const props = defineProps<{
  pdfUrl?: string
}>()

// 从 public/pdfjs 加载 PDF.js 库
let PDFJS: any = null
let isPdfJsLoaded = false

const scale = ref<number>(1.0)
const currentPage = ref<number>(1)
const totalPages = ref<number>(0)
const canvasRefs = new Map<number, HTMLCanvasElement>()
let pdfDoc: any = null
let pdfLoadingTask: any = null
const isDestroyed = ref<boolean>(false)
const isLoading = ref<boolean>(false)

const safeCleanup = async () => {
  try {
    // 1. 中止正在进行的PDF加载任务
    if (pdfLoadingTask && pdfLoadingTask.destroy) {
      pdfLoadingTask.destroy()
      pdfLoadingTask = null
    }

    // 2. 清理PDF文档实例
    if (pdfDoc && pdfDoc.destroy) {
      await pdfDoc.destroy()
      pdfDoc = null
    }

    // 3. 清除所有canvas内容
    canvasRefs.forEach((canvas, key) => {
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      canvas.height = 0
      canvas.width = 0
    })
    canvasRefs.clear()

    // 4. 重置状态
    totalPages.value = 0
    currentPage.value = 1
  } catch (error) {
    console.warn('PDF清理过程中出现警告:', error)
    // 清理过程中出现的错误不影响后续操作，继续执行
  }
}

// 新增：安全的PDF渲染函数
const safeRenderPdf = async (pdfUrl: string) => {
  // 检查组件是否已被销毁
  if (isDestroyed.value) {
    console.log('组件已销毁，停止渲染')
    return
  }

  if (!isPdfJsLoaded || !PDFJS) {
    await loadPdfJS()
    if (isDestroyed.value) return // 再次检查
  }

  // 开始加载前先清理旧数据
  await safeCleanup()

  // 开始加载时显示加载状态
  isLoading.value = true

  try {
    // 创建加载任务
    pdfLoadingTask = PDFJS.getDocument({
      url: pdfUrl,
      cMapUrl: 'https://unpkg.com/pdfjs-dist@3.4.120/cmaps/',
      cMapPacked: true,
      disableFontFace: false,
      withCredentials: false
    })

    // 等待PDF文档加载
    pdfDoc = await pdfLoadingTask.promise

    // 再次检查组件是否已销毁
    if (isDestroyed.value) {
      await safeCleanup()
      return
    }

    totalPages.value = pdfDoc.numPages
    currentPage.value = 1

    // 循环渲染所有页面
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      // 每次渲染前检查组件状态
      if (isDestroyed.value) {
        await safeCleanup()
        return
      }

      const page = await pdfDoc.getPage(pageNum)
      const viewport = page.getViewport({ scale: scale.value })
      const canvas = canvasRefs.get(pageNum)
      if (!canvas) continue

      // 清空画布
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }

      canvas.height = viewport.height
      canvas.width = viewport.width
      await page.render({ canvasContext: ctx!, viewport }).promise
    }
  } catch (error) {
    // 检查是否是用户主动中止
    if (error && error.name === 'AbortException') {
      console.log('PDF加载被用户中止')
      return
    }

    // 检查组件是否已销毁
    if (isDestroyed.value) {
      console.log('PDF加载过程中组件已销毁')
      return
    }

    console.error('PDF加载/渲染失败：', error)
    if (!isDestroyed.value) {
      message.error('加载超时，请重新加载')
    }
  } finally {
    // 只在组件未被销毁时更新状态
    if (!isDestroyed.value) {
      isLoading.value = false
    }
  }
}

// 加载 PDF.js 库
const loadPdfJS = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      PDFJS = window.pdfjsLib
      isPdfJsLoaded = true
      resolve()
      return
    }

    // 如果已经在加载中，等待加载完成
    if (isPdfJsLoaded) {
      resolve()
      return
    }

    try {
      // 动态加载 PDF.js 库
      const script = document.createElement('script')
      script.src = '/pdfjs/pdf.min.js'
      script.onload = () => {
        if (window.pdfjsLib) {
          PDFJS = window.pdfjsLib
          isPdfJsLoaded = true

          // 设置 worker 路径（使用 .js 文件而不是 .mjs）
          PDFJS.GlobalWorkerOptions.workerSrc = '/pdfjs/pdf.worker.min.js'

          console.log('PDF.js 库加载成功')
          resolve()

          // 如果有 PDF URL，则渲染
          if (props.pdfUrl) {
            reloadPdf()
          }
        } else {
          reject(new Error('PDF.js 库加载失败'))
        }
      }
      script.onerror = (error) => {
        console.error('加载 PDF.js 库失败：', error)
        message.error('PDF 查看器加载失败')
        reject(error)
      }
      document.head.appendChild(script)
    } catch (error) {
      console.error('加载 PDF.js 库失败：', error)
      message.error('PDF 查看器加载失败')
      reject(error)
    }
  })
}

const renderPdf = async (pdfUrl: string) => {
  if (!isPdfJsLoaded || !PDFJS) {
    await loadPdfJS()
  }

  // 开始加载时显示加载状态
  isLoading.value = true
  try {
    pdfDoc = await PDFJS.getDocument({
      url: pdfUrl,
      cMapUrl: 'https://unpkg.com/pdfjs-dist@3.4.120/cmaps/',
      cMapPacked: true,
      disableFontFace: false,
      withCredentials: false
    }).promise

    totalPages.value = pdfDoc.numPages
    currentPage.value = 1

    // 循环渲染所有页面
    for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum)
      const viewport = page.getViewport({ scale: scale.value })
      const canvas = canvasRefs.get(pageNum)
      if (!canvas) continue

      // 清空画布
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }

      canvas.height = viewport.height
      canvas.width = viewport.width
      await page.render({ canvasContext: ctx!, viewport }).promise
    }
  } catch (error) {
    console.error('PDF加载/渲染失败：', error)
    message.error('加载超时，请重新加载')
  } finally {
    // 所有页面渲染完成后隐藏加载状态
    isLoading.value = false
  }
}

const reloadPdf = async () => {
  // 重置销毁标志
  isDestroyed.value = false

  // 先清理旧数据
  await safeCleanup()

  // 渲染新PDF
  if (props.pdfUrl) {
    await safeRenderPdf(props.pdfUrl)
  }
}

onMounted(() => {
  isDestroyed.value = false
})

watch(
  () => props.pdfUrl,
  (newUrl, oldUrl) => {
    // 如果URL没有变化，不执行任何操作
    if (newUrl === oldUrl) {
      return
    }

    if (newUrl) {
      reloadPdf()
    } else {
      // URL为空时清理所有内容
      safeCleanup()
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  // 标记组件已销毁
  isDestroyed.value = true

  // 执行清理
  safeCleanup()
})
</script>

<style scoped lang="scss">
.pdf-preview-container {
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
  position: relative; /* 用于加载遮罩定位 */

  .pdf-content {
    width: 100%;
    height: 700px;
    overflow-y: auto;
    text-align: center;
    canvas {
      margin-bottom: 8px;
      border: 1px solid #eee;
    }
  }

  // 加载状态样式
  .loading-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1890ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  .loading-text {
    margin-top: 16px;
    color: #666;
    font-size: 14px;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
}
</style>
